import VALUE from 'core/data-model/constants/values'
import { _lookUpBasicPremiumRate } from 'core/service/basic-plan/premium'
import { d } from 'core/service/lib/decimal'
import { roundDown, round } from 'core/service/lib/number-format'

/**
 * createLoanPremiumService
 * @param {ReturnType<import('core/data/data-wrapper')._DB>} dataWrapper
 * @param {ReturnType<import('core/service/basic-plan/premium').createBasicPlanPremiumService>} basicPlanPremiumService
 */
export function createLoanPremiumService(dataWrapper, basicPlanPremiumService) {
  const _lookUpLoanPremiumRate = (premiumRates, productCode, planCode, gender, age) => {
    return _lookUpBasicPremiumRate(premiumRates, `${productCode}_${planCode}`, gender, age)
  }

  const lookUpLoanPremiumRate = (productCode, planCode, gender, age) => {
    return dataWrapper.getPremiumRates().then((premiumRates) => {
      return _lookUpLoanPremiumRate(premiumRates, productCode, planCode, gender, age)
    })
  }

  const calculatePremiumForCashPaymentMethod = (sumAssured, premiumRate, discount) => {
    const sumAssuredDecimal = d(sumAssured)
    const premiumRateDecimal = d(premiumRate)
    const discountDecimal = d(discount)
    const discountFactor = d(1).minus(discountDecimal)
    const totalPremium = roundDown(premiumRateDecimal.times(discountFactor), 2)
    const rawPremium = sumAssuredDecimal.times(d(totalPremium))

    return round(rawPremium.dividedBy(d(1000)), 2)
  }

  const calculatePremiumForLoanPaymentMethod = (sumAssured, premiumRate) => {
    const premiumRateDecimal = d(premiumRate)
    const sumAssuredDecimal = d(sumAssured)
    const rawPremium = premiumRateDecimal.times(sumAssuredDecimal)
    return roundDown(rawPremium.dividedBy(d(1000)), 2)
  }

  const convertToLoanPremiumRate = (productCode, cashPremiumRate, discount = 0) => {
    const cashPremiumRateDecimal = d(cashPremiumRate)

    const isIncludedDiscount = productCode !== VALUE.TMRTA

    const calculatedPremiumRate = isIncludedDiscount
      ? d(round(cashPremiumRateDecimal.times(1 - discount), 2))
      : cashPremiumRateDecimal

    const divider = d(1000).minus(calculatedPremiumRate)
    const rawLoanPremiumRate = calculatedPremiumRate.dividedBy(divider).times(1000)

    return round(rawLoanPremiumRate, 4)
  }

  const _calculatePremium = (sumAssured, premiumRate, productCode, paymentMethod, props) => {
    const zeroDiscount = 0

    const {
      calculatePremiumForLoanPaymentMethod,
      calculatePremiumForCashPaymentMethod,
      convertToLoanPremiumRate,
    } = props

    if (paymentMethod === VALUE.CASH) return calculatePremiumForCashPaymentMethod(sumAssured, premiumRate, zeroDiscount)

    const loanPremiumRate = convertToLoanPremiumRate(productCode, premiumRate, zeroDiscount)
    return calculatePremiumForLoanPaymentMethod(sumAssured, loanPremiumRate)
  }

  const calculatePremium = async (props) => {
    const { sumAssured, productCode, paymentMethod, planCode, insured } = props

    const premiumRate = await lookUpLoanPremiumRate(productCode, planCode, insured.gender, insured.age)

    return _calculatePremium(sumAssured, premiumRate, productCode, paymentMethod, {
      calculatePremiumForCashPaymentMethod,
      convertToLoanPremiumRate,
      calculatePremiumForLoanPaymentMethod,
    })
  }

  return {
    _lookUpLoanPremiumRate,
    lookUpLoanPremiumRate,
    calculatePremiumForCashPaymentMethod,
    calculatePremiumForLoanPaymentMethod,
    convertToLoanPremiumRate,
    _calculatePremium,
    calculatePremium,
  }
}
